import * as React from 'react';
import {
  CRM_PRODUCT_GROUPS_LABEL,
  CRM_PRODUCT_GROUPS_TOUR_TARGET,
  isPrimaryProfileLabel,
  parseProfileKeyValueText,
  splitBlocksForCrmGrid,
  type ProfileContentLayout,
  type ProfileTextBlock,
} from './profileText';

type RegisterTourTarget = (id: string, el: HTMLElement | null) => void;

function FieldBlock({
  block,
  hideEmptyValue = false,
  registerTourTarget,
}: {
  block: Extract<ProfileTextBlock, { type: 'field' }>;
  hideEmptyValue?: boolean;
  registerTourTarget?: RegisterTourTarget;
}) {
  const isCrmProductGroups =
    block.label.trim().toLowerCase() === CRM_PRODUCT_GROUPS_LABEL.toLowerCase();

  const labelRef = React.useCallback(
    (el: HTMLDivElement | null) => {
      if (isCrmProductGroups) {
        registerTourTarget?.(CRM_PRODUCT_GROUPS_TOUR_TARGET, el);
      }
    },
    [isCrmProductGroups, registerTourTarget],
  );

  return (
    <div>
      <div
        ref={isCrmProductGroups ? labelRef : undefined}
        data-tour-target={isCrmProductGroups ? CRM_PRODUCT_GROUPS_TOUR_TARGET : undefined}
        className="text-body1 font-medium text-neutral-text-800"
      >
        {block.label}
      </div>
      {hideEmptyValue && !block.value && !block.placeholder ? null : (
        <div
          className={
            block.placeholder
              ? 'mt-0.5 whitespace-pre-wrap text-body1 italic text-neutral-text-600'
              : 'mt-0.5 whitespace-pre-wrap text-body1 text-neutral-text-900'
          }
        >
          {block.value || '—'}
        </div>
      )}
    </div>
  );
}

function renderBlock(
  block: ProfileTextBlock,
  idx: number,
  {
    hideEmptyValue = false,
    registerTourTarget,
  }: {
    hideEmptyValue?: boolean;
    registerTourTarget?: RegisterTourTarget;
  } = {},
) {
  return block.type === 'field' ? (
    <FieldBlock
      key={`field-${idx}-${block.label}`}
      block={block}
      hideEmptyValue={hideEmptyValue}
      registerTourTarget={registerTourTarget}
    />
  ) : (
    <div key={`paragraph-${idx}`} className="whitespace-pre-wrap text-body1 text-neutral-text-900">
      {block.text}
    </div>
  );
}

export function ProfileStructuredContent({
  value,
  layout = 'default',
  twoColumns = false,
  registerTourTarget,
}: {
  value: string;
  layout?: ProfileContentLayout;
  /** @deprecated Prefer `layout="twoColumns"` */
  twoColumns?: boolean;
  registerTourTarget?: RegisterTourTarget;
}) {
  const resolvedLayout: ProfileContentLayout = twoColumns && layout === 'default' ? 'twoColumns' : layout;
  const parsed = parseProfileKeyValueText(value, {
    promoteEmptyColonLabels: resolvedLayout === 'sectioned',
  });

  if (parsed.mode === 'plain') {
    return (
      <div className="mt-1 whitespace-pre-wrap text-body1 text-neutral-text-900">{parsed.text}</div>
    );
  }

  if (parsed.blocks.length === 0) {
    return null;
  }

  const blockOpts = { registerTourTarget };

  if (resolvedLayout === 'twoColumns') {
    const primary = parsed.blocks.filter(
      (block) => block.type === 'field' && isPrimaryProfileLabel(block.label),
    );
    const secondary = parsed.blocks.filter(
      (block) => !(block.type === 'field' && isPrimaryProfileLabel(block.label)),
    );

    return (
      <div className="mt-1 grid grid-cols-1 gap-x-8 gap-y-2 sm:grid-cols-2">
        <div className="space-y-2">{primary.map((block, idx) => renderBlock(block, idx, blockOpts))}</div>
        <div className="space-y-2">{secondary.map((block, idx) => renderBlock(block, idx, blockOpts))}</div>
      </div>
    );
  }

  if (resolvedLayout === 'crmGrid') {
    const { identity, crm } = splitBlocksForCrmGrid(parsed.blocks);
    return (
      <div className="mt-1 space-y-2">
        {identity.length > 0 ? (
          <div className="space-y-2">{identity.map((block, idx) => renderBlock(block, idx, blockOpts))}</div>
        ) : null}
        {crm.length > 0 ? (
          <div className="grid grid-cols-1 gap-x-8 gap-y-2 sm:grid-cols-2">
            {crm.map((block, idx) => renderBlock(block, idx, blockOpts))}
          </div>
        ) : null}
      </div>
    );
  }

  if (resolvedLayout === 'sectioned') {
    return (
      <div className="mt-1 space-y-2">
        {parsed.blocks.map((block, idx) =>
          renderBlock(block, idx, { hideEmptyValue: true, registerTourTarget }),
        )}
      </div>
    );
  }

  return (
    <div className="mt-1 space-y-2">
      {parsed.blocks.map((block, idx) => renderBlock(block, idx, blockOpts))}
    </div>
  );
}
